import { Inject, Injectable } from '@angular/core'
import { NavigationStart, Router } from '@angular/router'
import { LineLayerSpecification } from 'maplibre-gl'
import { Subject, takeUntil } from 'rxjs'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { Layers } from '../layers-config/layers'
import { MapIcons } from 'src/app/core/enums/map-icons.enum'
import { DataRoadCoverageService } from '../services/data-road-coverage.service'
import { MapService } from 'src/app/core/services/map.service'
import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'

@Injectable()
export class LayerRoadCoverageService {
  private layer = this.layerPrefix
  public layerLinesId = `${this.layer}-lines`

  public sourceLineId = `${this.layer}-line-source`

  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  private timespan: number | undefined
  public selectedFeatureId: string = ''
  public selectedSegmentId: string = ''
  public layerInitialized: boolean = false
  public onDestroy: boolean = false
  public whileReload = false

  private roadCoverageSegmentLayerSpecification: LineLayerSpecification = {
    id: this.layerLinesId,
    type: 'line',
    minzoom: 8,
    source: this.sourceLineId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },

    paint: {
      'line-color': [
        'interpolate',
        ['linear'],
        ['get', 'coverage'],
        0,
        '#e9f8f9',
        5,
        '#cae4e7',
        7,
        '#a7ced3',
        10,
        '#87babf',
        20,
        '#62a6ac',
        100,
        '#3c909c',
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        6,
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        6,
        2,
      ],
    },
  }

  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layerPrefix: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataRoadCoverageService,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  private async initializeLayer() {
    this.mapService.map.addSource(this.sourceLineId, {
      ...emptyGeoJsonSource,
      generateId: true
    })

    this.mapService.map.addLayer(this.roadCoverageSegmentLayerSpecification)
    this.mapEventsService.registerForMapEvents({ layerId: this.layerLinesId }, true)

    this.layerInitialized = true

    // Immediately update the layer properties with the correct selectedFeatureId
    if (this.selectedFeatureId && this.selectedFeatureId !== '') {
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
    this.evaluateState()
  }

  ngOnDestroy() {
    this.onDestroy = true

    this.mapService.map.removeLayer(this.layerLinesId)

    this.mapEventsService.detachMapEvents({ layerId: this.layerLinesId })
    this.mapService.map.removeSource(this.sourceLineId)

    // Cancel all requests and clean up properly
    this.cancelRequests()
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  // Update your cancelRequests method to properly cancel HTTP requests
  private cancelRequests() {
    // Cancel requests in the data service
    this.dataService.cancelCurrentRequests()
  }

  // Update your reload method to handle cancellation gracefully
  public async reload() {
    // Cancel any previous requests first
    this.cancelRequests()

    if (this.mapService.isMapReady && this.mapService.map.getZoom() >= this.minZoom) {
      await this.dataService.loadRoadCoverageData(
        this.appState.isHistoryEnabled(),
        this.mapService.getExpandedBounds(0.5),
        this.appState.selectTimestamp(),
      )
      this.updateLayer()
    }
  }

  public async updateLayer() {
    if (this.mapService.isMapReady && this.whileReload == false) {
      const roadCoverage = this.dataService.getRoadCoverage()
      this.mapService.setGeoJsonData(this.sourceLineId, roadCoverage)
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private async evaluateState() {
    const layers = this.appState.selectLayers()
    if (layers.some((layer) => layer === Layers.RoadCoverage.name)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }
    // if layer is not visible stop all events
    if (this.isLayerVisible === false) {
      return
    }

    if (!this.timestamp) this.timestamp = this.appState.selectTimestamp()

    if (!this.dataService.isDataInitialized) {
      await this.reload()
    }

    if (
      this.appState.hasLayerVisibilityChanged(this.layer) &&
      !this.appState.getState().historyEnabled
    ) {
      await this.reload()
    }

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
      await this.reload()
    }

    if (this.appState.hasTimestampChanged()) {
      this.timestamp = this.appState.selectTimestamp()
      await this.reload()
    }

    if (
      this.appState.hasSelectedFeatureIdChanged() &&
      this.appState.getState().selectedFeatureId !== ''
    ) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.updateLayer()
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      this.mapService.setLayerVisibility(this.layerLinesId, isVisible)
      if (!isVisible) {
        // Cancel any ongoing requests when layer becomes invisible
        this.cancelRequests()
      }
    }
  }

  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return

    this.mapService.map.setPaintProperty(this.layerLinesId, 'line-width', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      6,
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      6,
      2,
    ])
  }

  private getSelectedFeatureExpression(
    selectedFeatureId: string,
  ): maplibregl.ExpressionSpecification {
    return ['==', ['get', 'way_id'], Number(selectedFeatureId)]
  }
}
